import { makeStyles } from '@material-ui/core/styles';

export const useStyles = makeStyles(theme => ({
  page: {
    width: '100%',
    minWidth: 0,
    padding: theme.spacing(5),
    color: 'var(--ds-color-neutral-900)',
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
  },
  back: {
    ...theme.typography.body1,
    alignSelf: 'flex-start',
    padding: 0,
    minWidth: 0,
    height: 28,
    color: 'var(--ds-color-neutral-900)',
    '& svg': { fontSize: 20 },
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  muted: { color: 'var(--ds-color-neutral-400)' },
  primary: { color: 'var(--ds-color-neutral-900)' },
  secondary: { color: 'var(--ds-color-neutral-600)' },
  tabs: {
    marginTop: theme.spacing(3),
    minHeight: 48,
    borderBottom: '1px solid var(--ds-color-neutral-200)',
    '& .MuiTabs-indicator': {
      backgroundColor: 'var(--ds-color-primary)',
      height: 2,
    },
  },
  tab: {
    ...theme.typography.h4,
    minWidth: 160,
    minHeight: 48,
    color: 'var(--ds-color-primary)',
    textTransform: 'none',
  },
  filters: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.25),
    '& > button': { marginRight: 0, maxWidth: '100%' },
    '& > button[aria-haspopup]': { maxWidth: 320 },
  },
  filterLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  openingSearch: { padding: theme.spacing(1, 2) },
  menuStatus: { padding: theme.spacing(1, 2), maxWidth: 320 },
  customRange: { padding: theme.spacing(2), maxWidth: '100%' },
  dateFields: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2) },
  dateField: { width: 160, '& > p': { marginBottom: theme.spacing(1) } },
  dateError: { marginTop: theme.spacing(1), color: 'var(--ds-color-danger)' },
  dateActions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  card: {
    marginTop: theme.spacing(2),
    background: 'var(--ds-color-white)',
    border: '1px solid var(--ds-color-neutral-200)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '200px minmax(0, 1fr) minmax(0, 1fr) 144px 152px',
    columnGap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    '& > *': { minWidth: 0, overflowWrap: 'anywhere' },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      rowGap: theme.spacing(1.5),
    },
  },
  listHeader: {
    background: 'var(--ds-color-neutral-50)',
    color: 'var(--ds-color-neutral-400)',
    [theme.breakpoints.down('sm')]: { display: 'none' },
  },
  loadingRow: { borderTop: '1px solid var(--ds-color-neutral-200)' },
  row: {
    borderTop: '1px solid var(--ds-color-neutral-200)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--ds-color-neutral-50)' },
    '&:focus-visible': {
      outline: '2px solid var(--ds-color-primary)',
      outlineOffset: -2,
    },
  },
  open: {
    background: 'var(--ds-color-brand-50)',
    boxShadow: 'inset 2px 0 0 var(--ds-color-primary)',
    '&:hover': { background: 'var(--ds-color-brand-50)' },
  },
  opening: {
    marginTop: theme.spacing(0.5),
    color: 'var(--ds-color-neutral-600)',
  },
  paragraph: {
    ...theme.typography.body2,
    color: 'var(--ds-color-neutral-900)',
    lineHeight: `${theme.spacing(2.5)}px`,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    '&$muted': { color: 'var(--ds-color-neutral-400)' },
  },
  question: {
    // Grid stretching exposes text past the clamp when the answer is taller.
    alignSelf: 'start',
    WebkitLineClamp: 2,
    [theme.breakpoints.down('sm')]: { gridColumn: '1 / -1', gridRow: 2 },
  },
  answer: {
    [theme.breakpoints.down('sm')]: { gridColumn: '1 / -1', gridRow: 3 },
  },
  answerText: { WebkitLineClamp: 3 },
  richStep: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  richStepKind: {
    ...theme.typography.overline,
    color: 'var(--ds-color-neutral-400)',
  },
  richStepText: { WebkitLineClamp: 2 },
  richFullText: {
    ...theme.typography.body2,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  richStepFull: {
    width: 296,
    maxWidth: '100%',
    '& $richOpening': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.5),
      borderRadius: 12,
      '& > div': { minWidth: 0, overflowWrap: 'anywhere' },
    },
  },
  richOpening: {
    padding: theme.spacing(0.75, 1),
    background: 'var(--ds-color-white)',
    borderRadius: theme.misc.borderRadius,
    border: '1px solid var(--ds-color-neutral-200)',
    '& $secondary': { marginTop: theme.spacing(0.25) },
  },
  date: {
    color: 'var(--ds-color-neutral-600)',
    [theme.breakpoints.down('sm')]: {
      gridColumn: 2,
      gridRow: 1,
      textAlign: 'right',
    },
  },
  channel: {
    marginTop: theme.spacing(0.5),
    color: 'var(--ds-color-neutral-400)',
  },
  badges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  badge: {
    ...theme.typography.overline,
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
    padding: theme.spacing(0.25, 1),
    borderRadius: 100,
    textTransform: 'none',
  },
  thumbsDown: {
    background: 'var(--ds-color-rose-50)',
    color: 'var(--ds-color-rose-800)',
  },
  knowledgeGap: {
    background: 'var(--ds-color-amber-50)',
    color: 'var(--ds-color-amber-800)',
  },
  action: {
    justifySelf: 'end',
    alignSelf: 'start',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
      justifySelf: 'start',
    },
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(8, 2),
    textAlign: 'center',
    borderTop: '1px solid var(--ds-color-neutral-200)',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    background: 'var(--ds-color-rose-50)',
    borderRadius: theme.misc.borderRadius,
    '& > svg': { color: 'var(--ds-color-danger)', fontSize: 16 },
  },
  retry: {
    ...theme.typography.body2,
    fontWeight: theme.typography.button.fontWeight,
    marginLeft: 'auto',
    flexShrink: 0,
    height: 24,
    minHeight: 24,
    color: 'var(--ds-color-primary)',
    padding: theme.spacing(0, 1),
  },
  more: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0, 5),
  },
  moreButton: {
    ...theme.typography.body2,
    fontWeight: theme.typography.button.fontWeight,
    height: 34,
    padding: theme.spacing(1, 2),
    borderRadius: theme.misc.borderRadius,
    borderColor: 'var(--ds-color-neutral-200)',
    color: 'var(--ds-color-neutral-900)',
  },
  skeleton: {
    height: 12,
    borderRadius: theme.shape.borderRadius,
    background: 'var(--ds-color-neutral-100)',
    marginBottom: theme.spacing(1),
    '&:last-child': { marginBottom: 0 },
  },
}));
